/**
 * Formata o nome do combustível de forma inteligente, abreviando progressivamente
 * até caber no limite de caracteres especificado.
 *
 * Exemplos:
 * - "Etanol" → "Etanol"
 * - "Etanol Aditivado" → "Et. Aditivado"
 * - "Gasolina Comum" → "G. Comum"
 * - "Diesel S10 Aditivado Promoção" → "D. S10 Adt. Promo"
 * - "G.V Power" → "G. V Power"
 */
export function formatFuelName(name: string, maxLength: number = 16): string {
  const lowerName = name.toLowerCase();

  // Verifica se tem "promoção" ou "promo" no nome
  const hasPromo = /\s*(promoção|promo|promocao|promoçao)\.?/i.test(name);

  // Normaliza o nome removendo textos extras como "promoção", "promo", etc.
  const normalizedName = lowerName
    .replace(/\s*(promoção|promo|promocao|promoçao)\.?/gi, '')
    .trim();

  // Função para capitalizar palavra
  const capitalize = (word: string) =>
    word.charAt(0).toUpperCase() + word.slice(1);

  // Função para abreviar uma palavra (3 primeiras letras + ponto)
  const abbreviate = (word: string) => {
    if (word.length <= 3) {
      return capitalize(word);
    }
    return word.charAt(0).toUpperCase() + word.slice(1, 3) + '.';
  };

  // Mapeamento de primeiras palavras para abreviação (quando há mais palavras)
  const firstWordAbbreviations: Record<string, string> = {
    gasolina: 'G.',
    diesel: 'D.',
    etanol: 'Et.',
    gnv: 'GNV',
  };

  const words = normalizedName.split(' ').filter(w => w.length > 0);
  if (words.length === 0) {
    return name;
  }

  // Monta o nome progressivamente, abreviando conforme necessário
  const buildName = (abbreviateLevel: number): string => {
    const parts: string[] = [];

    for (let i = 0; i < words.length; i++) {
      const word = words[i];

      if (i === 0) {
        // Primeira palavra: verifica se tem abreviação conhecida
        const abbr = firstWordAbbreviations[word];
        if (abbr && words.length > 1) {
          // Só usa abreviação se tiver mais palavras (ex: "Etanol" sozinho fica "Etanol")
          parts.push(abbr);
        } else if (abbreviateLevel >= 1 && words.length > 1) {
          // Abrevia para inicial + ponto
          parts.push(word.charAt(0).toUpperCase() + '.');
        } else {
          parts.push(capitalize(word));
        }
      } else {
        // Palavras seguintes: decide se abrevia baseado no nível
        if (word.length <= 3) {
          // Palavras curtas (S10, V, etc.) sempre mantém
          parts.push(word.toUpperCase());
        } else if (abbreviateLevel >= 2) {
          // Nível 2+: abrevia todas as palavras longas
          parts.push(abbreviate(word));
        } else {
          // Mantém a palavra capitalizada
          parts.push(capitalize(word));
        }
      }
    }

    let result = parts.join(' ');

    // Adiciona Promo se necessário
    if (hasPromo) {
      result += ' Promo';
    }

    return result;
  };

  // Tenta com diferentes níveis de abreviação até caber
  for (let level = 0; level <= 3; level++) {
    const result = buildName(level);
    if (result.length <= maxLength) {
      return result;
    }
  }

  // Se ainda não coube, retorna a versão mais abreviada
  return buildName(3);
}

export default formatFuelName;
